/**
 * useIntegrationInsights
 *
 * Fetches server-computed integration insights from
 * GET /bpa/integrations/insights/ and writes the result into
 * `integrationInsightsAtom`.
 *
 * Mirrors the pattern used by `useIntegrationListing`:
 *  - Waits for query-string filter initialisation before first fetch.
 *  - Re-fetches when the company changes or when `integrationDataRefreshAtom`
 *    is written (e.g. the manual grid refresh button).
 *  - Cancels stale responses via the `cancelled` guard.
 */

import { useAtom, useAtomValue } from 'jotai';
import { showFailureToast } from 'lib/ToastHelper';
import { useEffect } from 'react';

import { fetchIntegrationInsights } from '../services/integrationListingService';
import {
  integrationInsightsAtom,
  integrationQueryParamsAtom,
  isQuerystringFiltersInitializedAtom,
} from '../state';

export function useIntegrationInsights() {
  // integrationQueryParamsAtom already subsumes companyId, all active filters,
  // ordering, pagination, and the refresh trigger — so a single dependency
  // covers every case that should cause the insights to re-fetch.
  const queryParams = useAtomValue(integrationQueryParamsAtom);
  const isInitialized = useAtomValue(isQuerystringFiltersInitializedAtom);
  const [, setInsights] = useAtom(integrationInsightsAtom);

  useEffect(() => {
    // Wait until URL query-string filters have been read and applied
    if (!isInitialized) return;

    let cancelled = false;

    const loadInsights = async () => {
      void setInsights((prev) => ({ ...prev, loading: true }));

      try {
        const response = await fetchIntegrationInsights(queryParams);

        if (!cancelled) {
          void setInsights({
            ...(response.data ?? {}),
            loading: false,
          });
        }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[useIntegrationInsights] fetch error:', err);
          showFailureToast('Failed to load integration insights.');
          void setInsights({
            total_integrations: 0,
            active_integrations: 0,
            aging_integrations: 0,
            aged_out_integration: 0,
            provider_count: 0,
            module_count: 0,
            region_count: 0,
            loading: false,
          });
        }
      }
    };

    void loadInsights();

    return () => {
      cancelled = true;
    };
  }, [queryParams, isInitialized, setInsights]);
}
